import { useMutation, useQuery } from '@apollo/client';
import { ShoppingCartOutlined } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  WidgetEditActions,
  useCustomSnackbar,
  CurrencyProvider,
} from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctSourcingCaseQuoteLineItemInput } from '@/generated/gql/graphql';
import { isSourcingCaseEditable } from '~/common/guards/sourcingCase.guards';
import { I18nNamespace } from '~/common/types';
import { WidgetContentEmpty } from '~/components/WidgetContentEmpty/WidgetContentEmpty';
import { useOpenClose } from '~/hooks';
import { useSourcingCase } from '~/modules/Module.hooks';

import { SourcingCaseQuote } from './components/SourcingCaseQuote/SourcingCaseQuote';
import { SourcingCaseQuoteHeader } from './components/SourcingCaseQuoteHeader/SourcingCaseQuoteHeader';
import { SOURCING_CASE_QUOTE_MUTATION } from './SourcingCaseQuoteWidget.mutation.gql';
import { SOURCING_CASE_QUOTE_QUERY } from './SourcingCaseQuoteWidget.query.gql';

export const SourcingCaseQuoteWidget = () => {
  const { id } = useSourcingCase();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'SourcingCaseQuoteWidget' });

  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();

  const variables = {
    id,
  };

  const { data, loading, error } = useQuery(SOURCING_CASE_QUOTE_QUERY, {
    variables,
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const isEditable = isSourcingCaseEditable(data?.tesseract.sourcingCase);

  const gridApiRef = useGridApiRef();

  const [update, { loading: saving }] = useMutation(SOURCING_CASE_QUOTE_MUTATION, {
    refetchQueries: [{ query: SOURCING_CASE_QUOTE_QUERY, variables }],
    onError,
    onCompleted: () => {
      enqueueSuccessSnackbar(t('saveSuccess'));
      onToggle();
    },
  });

  const [{ open }, _, onToggle] = useOpenClose();

  const quote = data?.tesseract.sourcingCase?.quote;

  const onSave = useCallback(() => {
    const rows = Array.from(gridApiRef?.current.getRowModels?.().values()).map(
      (row): TsrctSourcingCaseQuoteLineItemInput => {
        return {
          status: row.status,
        };
      },
    );
    update({
      variables: {
        id,
        data: {
          lineItems: rows,
        },
      },
    });
  }, [gridApiRef, update, id]);

  let content: React.ReactNode;

  if (!error) {
    if (loading || quote) {
      content = (
        <CurrencyProvider value={quote?.currency || DEFAULT_CURRENCY}>
          <Stack>
            <SourcingCaseQuoteHeader data={quote} />
            <SourcingCaseQuote data={quote} loading={loading} viewOnly={!open} gridApiRef={gridApiRef} />
          </Stack>
        </CurrencyProvider>
      );
    } else {
      content = <WidgetContentEmpty title={t('empty')} />;
    }
  } else {
    content = <WidgetContentEmpty title={t('error')} color="error.main" />;
  }

  return (
    <Widget hover={false} data-intercom-target="SourcingCaseQuoteWidget">
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<ShoppingCartOutlined fontSize="small" />} />}
        actionsRight={
          <WidgetEditActions onSave={onSave} open={open} saving={saving} disabled={!isEditable} onToggle={onToggle} />
        }
      />
      <WidgetContent>{content}</WidgetContent>
    </Widget>
  );
};
